import React, { useEffect, useMemo, useRef, useState } from 'react';
import Layout from '../components/Layout';
import { dictionaryAPI } from '../services/api';
import { t } from '../utils/i18n';

function Dictionary() {
    const [terms, setTerms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState('');
    const [filter, setFilter] = useState('all');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const [, forceUpdate] = useState(0);
    const searchRef = useRef(null);
    const boxRef = useRef(null);

    useEffect(() => {
        const handleLanguageChange = () => forceUpdate((n) => n + 1);
        window.addEventListener('languageChanged', handleLanguageChange);
        return () => window.removeEventListener('languageChanged', handleLanguageChange);
    }, []);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                setLoading(true);
                const response = await dictionaryAPI.getTerms();
                if (!cancelled && response.data.success) {
                    setTerms(response.data.terms || []);
                }
            } catch (error) {
                console.error('Error loading dictionary:', error);
                if (!cancelled) setTerms([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (boxRef.current && !boxRef.current.contains(event.target)) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const matchesQuery = (term, q) => {
        if (!q) return true;
        return term.english.toLowerCase().includes(q) ||
            term.eastern.toLowerCase().includes(q) ||
            term.western.toLowerCase().includes(q);
    };

    const matchesFilter = (term) => {
        if (filter === 'technical') return term.technical === true;
        if (filter === 'snap') return Array.isArray(term.sources) && term.sources.includes('snap');
        if (filter === 'scratch') return Array.isArray(term.sources) && term.sources.includes('scratch');
        return true;
    };

    const scoped = useMemo(() => terms.filter(matchesFilter), [terms, filter]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return scoped;
        return scoped.filter((term) => matchesQuery(term, q));
    }, [scoped, query]);

    const suggestions = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return [];
        const starts = [];
        const contains = [];
        for (const term of scoped) {
            const en = term.english.toLowerCase();
            const east = term.eastern.toLowerCase();
            const west = term.western.toLowerCase();
            if (en.startsWith(q) || east.startsWith(q) || west.startsWith(q)) {
                starts.push(term);
            } else if (`${en} ${east} ${west}`.includes(q)) {
                contains.push(term);
            }
            if (starts.length >= 12) break;
        }
        return [...starts, ...contains].slice(0, 12);
    }, [scoped, query]);

    const chooseSuggestion = (term) => {
        setQuery(term.english);
        setShowSuggestions(false);
        setActiveIndex(-1);
        if (searchRef.current) searchRef.current.focus();
    };

    const handleKeyDown = (event) => {
        if (!showSuggestions || suggestions.length === 0) return;
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setActiveIndex((i) => (i + 1) % suggestions.length);
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
        } else if (event.key === 'Enter' && activeIndex >= 0) {
            event.preventDefault();
            chooseSuggestion(suggestions[activeIndex]);
        } else if (event.key === 'Escape') {
            setShowSuggestions(false);
        }
    };

    const highlight = (text, q) => {
        if (!q || !text) return text;
        const idx = text.toLowerCase().indexOf(q.toLowerCase());
        if (idx < 0) return text;
        return (
            <>
                {text.slice(0, idx)}
                <mark style={{ background: 'rgba(207, 155, 6, 0.28)', color: 'inherit', padding: 0 }}>{text.slice(idx, idx + q.length)}</mark>
                {text.slice(idx + q.length)}
            </>
        );
    };

    if (loading) {
        return (
            <Layout>
                <div style={{ textAlign: 'center', padding: '50px' }}>
                    <div className="loading" style={{ width: '40px', height: '40px', margin: '0 auto' }}></div>
                </div>
            </Layout>
        );
    }

    const q = query.trim();

    return (
        <Layout>
            <div className="content-header">
                <h1 className="page-title">{t('dictionary')}</h1>
                <p className="page-description">{t('dictionaryDescription')}</p>
            </div>

            <div style={{
                display: 'flex',
                gap: '12px',
                alignItems: 'stretch',
                marginBottom: '24px',
                flexWrap: 'wrap'
            }}>
                <div ref={boxRef} style={{ position: 'relative', flex: '1 1 280px', minWidth: 0 }}>
                    <input
                        ref={searchRef}
                        type="search"
                        className="form-input"
                        value={query}
                        autoComplete="off"
                        placeholder={t('dictionarySearchPlaceholder')}
                        onChange={(e) => {
                            setQuery(e.target.value);
                            setShowSuggestions(true);
                            setActiveIndex(-1);
                        }}
                        onFocus={() => setShowSuggestions(true)}
                        onKeyDown={handleKeyDown}
                        style={{
                            width: '100%',
                            fontSize: '18px',
                            padding: '16px 20px',
                            borderRadius: '8px'
                        }}
                    />
                    {showSuggestions && suggestions.length > 0 && (
                        <div style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            marginTop: '4px',
                            background: 'white',
                            border: '1px solid var(--border-color)',
                            borderRadius: '8px',
                            boxShadow: 'var(--shadow-lg)',
                            zIndex: 20,
                            maxHeight: '320px',
                            overflowY: 'auto'
                        }}>
                            {suggestions.map((term, index) => (
                                <button
                                    type="button"
                                    key={term.english}
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        chooseSuggestion(term);
                                    }}
                                    style={{
                                        display: 'block',
                                        width: '100%',
                                        textAlign: 'left',
                                        padding: '10px 16px',
                                        border: 'none',
                                        borderBottom: '1px solid var(--border-color)',
                                        background: index === activeIndex ? 'rgba(207, 155, 6, 0.1)' : 'white',
                                        cursor: 'pointer',
                                        fontSize: '14px'
                                    }}
                                >
                                    <div style={{ fontWeight: 600 }}>{highlight(term.english, q)}</div>
                                    <div className="armenian-text" style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
                                        {[term.eastern, term.western].filter(Boolean).join(' · ')}
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                <select
                    className="form-select"
                    value={filter}
                    onChange={(e) => {
                        setFilter(e.target.value);
                        setShowSuggestions(false);
                        setActiveIndex(-1);
                    }}
                    aria-label={t('dictionary')}
                    style={{
                        flex: '0 0 200px',
                        fontSize: '16px',
                        padding: '16px 16px',
                        borderRadius: '8px',
                        fontWeight: 500
                    }}
                >
                    <option value="all">{t('dictionaryFilterAll')}</option>
                    <option value="technical">{t('dictionaryFilterTechnical')}</option>
                    <option value="snap">{t('dictionaryFilterSnap')}</option>
                    <option value="scratch">{t('dictionaryFilterScratch')}</option>
                </select>
            </div>

            <div className="table-container">
                <div className="table-header">
                    <h2 className="table-title">
                        {q ? t('dictionaryResults') : t('dictionaryTerms')}
                    </h2>
                    <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
                        {filtered.length}
                    </span>
                </div>
                {filtered.length === 0 ? (
                    <div style={{ padding: '40px 24px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                        {scoped.length === 0 ? t('dictionaryEmpty') : t('dictionaryNoResults')}
                    </div>
                ) : (
                    <div className="table-responsive">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>{t('originalEnglish')}</th>
                                    <th className="armenian-text">{t('easternArmenian')}</th>
                                    <th className="armenian-text">{t('westernArmenian')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((term) => (
                                    <tr key={term.english}>
                                        <td>{highlight(term.english, q)}</td>
                                        <td className="armenian-text" lang="hy">{highlight(term.eastern, q)}</td>
                                        <td className="armenian-text" lang="hyw">{highlight(term.western, q)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </Layout>
    );
}

export default Dictionary;
